import { createWebStorage } from '@dcs/platform/web';
import { createWebTracker } from '@dcs/platform/track';
import type { Tracker } from '@dcs/platform/track';
import type { UsageEvent } from '@dcs/contracts';
import { createContext, use, useEffect, useRef, type ReactNode } from 'react';

/**
 * Anonymous usage tracking (F7 D-2): `useTrack()` returns `(event) => void`. Outside a provider it is
 * a no-op, so features and their tests need no setup. The only identifier sent is a random device id
 * (`@dcs/platform/track`); events carry a kind, a license class and a pass flag, nothing else.
 */

/** Same origin: the Pages Function forwards `/api/*` to the Worker, so no CORS and no `API_URL`. */
export const TRACK_ENDPOINT = '/api/track';

export type TrackFn = (event: UsageEvent) => void;

const noop: TrackFn = () => undefined;

const TrackContext = createContext<TrackFn>(noop);

/**
 * A tracker that is built on the first event: the app registers no page listeners and creates no
 * device id for a session that never starts an exam.
 */
export function createLazyTracker(create?: () => Tracker): Tracker {
  const make =
    create ?? (() => createWebTracker({ endpoint: TRACK_ENDPOINT, storage: createWebStorage() }));
  let tracker: Tracker | null = null;
  return {
    track(event) {
      tracker ??= make();
      tracker.track(event);
    },
    flush() {
      tracker?.flush();
    },
  };
}

export function TrackProvider({ tracker, children }: { tracker: Tracker; children: ReactNode }) {
  return <TrackContext value={tracker.track}>{children}</TrackContext>;
}

export function useTrack(): TrackFn {
  return use(TrackContext);
}

/** Sends `event` once when the component mounts (StrictMode's second effect run is ignored). */
export function useTrackOnce(event: UsageEvent): void {
  const track = useTrack();
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    track(event);
    // The event describes the moment of mounting; later changes of its identity do not resend it.
  }, []);
}

/** Renders nothing; sends `event` once when it appears (e.g. in a screen's finished branch). */
export function TrackOnce({ event }: { event: UsageEvent }): null {
  useTrackOnce(event);
  return null;
}
